import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { ACCOUNTING_QUERY_KEYS } from "@/features/accounting/configs/queryKeys";
import { accountingService } from "@/features/accounting/services/accounting.service";
import { ACTIVITY_OPERATIONS_QUERY_KEYS } from "@/features/activity-operations/configs/queryKeys";
import { REFUND_REQUESTS_QUERY_KEYS } from "@/features/refund-requests/configs/queryKeys";
import {
  broadcastBridgeTransferResult,
  subscribeToBridgeTransferResults,
} from "@/shared/utils/bridgeTransferChannel";
import { isConflictError } from "@/shared/utils/errorHandling";

/**
 * Laps laissé à la fenêtre de consentement pour résoudre l'issue elle-même.
 * Passé ce délai sans phase `confirmed`, elle a été fermée avant d'y arriver :
 * cet onglet, qui reste vivant, prend le relais.
 */
const RESOLVE_TAKEOVER_DELAY_MS = 2500;

/**
 * Répercute dans l'onglet d'origine l'issue d'un virement Bridge, et la résout
 * quand personne d'autre ne l'a fait.
 *
 * Les URLs de callback ne portent plus le statut : revenir de Bridge ne prouve
 * de Bridge ne prouve donc rien. C'est `bridge_payment_status` qui tranche, et
 * c'est cet onglet qui le demande — la popup meurt souvent avant. Consentement
 * donné → on enregistre la date d'émission ; jamais consenti ou refusé → on
 * libère le hold sur le mouvement et rien n'est soldé (ADM-864).
 */
const BridgeTransferListener = () => {
  const queryClient = useQueryClient();
  const takeoverTimersRef = useRef(new Map<string, number>());

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
    queryClient.invalidateQueries({ queryKey: ACTIVITY_OPERATIONS_QUERY_KEYS.all });
    queryClient.invalidateQueries({ queryKey: REFUND_REQUESTS_QUERY_KEYS.all });
  }, [queryClient]);

  useEffect(() => {
    const timers = takeoverTimersRef.current;

    const clearTakeover = (movementId: string) => {
      const timerId = timers.get(movementId);
      if (timerId !== undefined) {
        window.clearTimeout(timerId);
        timers.delete(movementId);
      }
    };

    /**
     * Demande l'issue à Bridge via l'API, puis agit : enregistrer la date
     * d'émission, ou libérer une initiation qui n'a jamais abouti. Le résultat
     * repart sur le canal, seul moyen pour le dialog et la form de règlement de
     * sortir de leur état d'attente.
     */
    const resolveOutcome = async (movementId: string, discardMovement: boolean, toastId: string) => {
      try {
        const status = await accountingService.bridgePaymentStatus(movementId);

        if (status.consented) {
          try {
            await accountingService.bridgeConfirm(movementId);
            broadcastBridgeTransferResult({
              movementId, phase: "confirmed", status: "success", issueDateRecorded: true,
            });
          }
          catch (error) {
            // eslint-disable-next-line no-console
            console.error("[BridgeTransfer] bridge_confirm échoué:", error);
            broadcastBridgeTransferResult({
              movementId, phase: "confirmed", status: "success", issueDateRecorded: false,
            });
          }
          return;
        }

        // Jamais consenti (CREA) ou refusé (RJCT) : aucun argent n'a bougé, donc
        // le hold doit tomber et aucune opération ne doit rester soldée.
        await accountingService.bridgeRelease(movementId, discardMovement);
        broadcastBridgeTransferResult({ movementId, phase: "confirmed", status: "failure" });
      }
      catch (error) {
        if (isConflictError(error)) {
          // Bridge atteste le consentement entre-temps : ne rien annuler.
          broadcastBridgeTransferResult({
            movementId, phase: "confirmed", status: "success", issueDateRecorded: false,
          });
          return;
        }
        // eslint-disable-next-line no-console
        console.error("[BridgeTransfer] résolution de l'issue échouée:", error);
        toast.warning(
          `L'issue du virement Bridge (mouvement ${movementId}) n'a pas pu être vérifiée. Reprenez-le depuis le mouvement bancaire.`,
          { id: toastId, duration: 10000 },
        );
        invalidate();
      }
    };

    const releaseAbandonedInitiation = async (
      movementId: string,
      discardMovement: boolean,
      toastId: string,
    ) => {
      try {
        const result = await accountingService.bridgeRelease(movementId, discardMovement);
        broadcastBridgeTransferResult({ movementId, phase: "released" });
        toast.warning(
          result.discarded
            ? `Virement Bridge non finalisé : le mouvement ${movementId} et son règlement ont été annulés.`
            : `Virement Bridge non finalisé : l'initiation du mouvement ${movementId} a été annulée, vous pouvez la relancer.`,
          { id: toastId, duration: 10000 },
        );
      }
      catch (error) {
        if (isConflictError(error)) {
          broadcastBridgeTransferResult({
            movementId, phase: "confirmed", status: "success", issueDateRecorded: false,
          });
        }
        else {
          // eslint-disable-next-line no-console
          console.error("[BridgeTransfer] libération de l'initiation échouée:", error);
          toast.warning(
            `La fenêtre Bridge a été fermée avant la confirmation (mouvement ${movementId}), et l'initiation n'a pas pu être annulée automatiquement.`,
            { id: toastId, duration: 10000 },
          );
        }
      }
      finally {
        invalidate();
      }
    };

    const unsubscribe = subscribeToBridgeTransferResults((message) => {
      const { movementId, phase } = message;
      // Un toast par mouvement : l'issue remplace l'attente au lieu de s'empiler.
      const toastId = `bridge-transfer-${movementId}`;

      switch (phase) {
        case "returned": {
          toast.loading(`Vérification du virement Bridge (mouvement ${movementId})...`, { id: toastId });
          const discardMovement = message.discardMovement === true;
          clearTakeover(movementId);
          timers.set(movementId, window.setTimeout(() => {
            timers.delete(movementId);
            void resolveOutcome(movementId, discardMovement, toastId);
          }, RESOLVE_TAKEOVER_DELAY_MS));
          return;
        }

        case "confirmed": {
          clearTakeover(movementId);
          if (message.status === "failure") {
            toast.warning(
              `Le virement Bridge n'a pas été validé (mouvement ${movementId}) : rien n'a été soldé.`,
              { id: toastId, duration: 10000 },
            );
          }
          else if (message.issueDateRecorded) {
            toast.success(`Virement Bridge confirmé (mouvement ${movementId})`, { id: toastId });
          }
          else {
            toast.warning(
              `Virement Bridge validé (mouvement ${movementId}), mais la date d'émission n'a pas pu être enregistrée.`,
              { id: toastId, duration: 10000 },
            );
          }
          invalidate();
          return;
        }

        case "abandoned": {
          clearTakeover(movementId);
          toast.loading("Annulation de l'initiation du virement Bridge...", { id: toastId });
          void releaseAbandonedInitiation(movementId, message.discardMovement === true, toastId);
          return;
        }

        case "released": {
          // Libération déjà effectuée et annoncée par l'émetteur : on rafraîchit.
          clearTakeover(movementId);
          invalidate();
          return;
        }
      }
    });

    return () => {
      unsubscribe();
      for (const timerId of timers.values()) window.clearTimeout(timerId);
      timers.clear();
    };
  }, [invalidate]);

  return null;
};

export default BridgeTransferListener;
